import { MetaLayout } from "@/common/MetaLayout";
import { Container } from "@/components/Container";
import PaperDetail from "@/components/PaperDetail";
import RelatedPapers from "@/components/RelatedPapers";
import ErrorPage from "@/pages/_error";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { useIntl } from "react-intl";
import { Environment, graphql } from "react-relay";
import { useParams } from "react-router";
import { PaperPageQuery as PaperPageQueryType } from "./__generated__/PaperPageQuery.graphql";

const DESCRIPTION_LENGTH = 160;

/** The arXiv version an identifier names: 2 for `2610.08786v2`. */
function versionOf(id: string): number | undefined {
  const match = /\dv(\d+)$/.exec(id);
  return match ? Number(match[1]) : undefined;
}

export default function PaperPage() {
  const intl = useIntl();
  const { id = "", number } = useParams();
  const version = versionOf(number ?? id);
  const {
    query: { paperByExternalId: paper },
  } = usePaperPageQuery();
  if (!paper) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({ defaultMessage: "Paper not found" })}
      />
    );
  }
  const shown =
    version === undefined
      ? undefined
      : paper.versions.find((candidate) => candidate.number === version);
  if (version !== undefined && paper.versions.length > 0 && !shown) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({ defaultMessage: "Version not found" })}
      />
    );
  }
  const title = shown?.title ?? paper.title;
  const description = (shown?.abstract ?? paper.abstract).replaceAll("$", "");
  const source = paper.arxivId
    ? `arXiv:${paper.arxivId}${shown ? `v${shown.number}` : ""}`
    : paper.venue;
  return (
    <MetaLayout
      metaTitle={source ? `${title} (${source})` : title}
      metaDescription={
        description.length > DESCRIPTION_LENGTH
          ? `${description.slice(0, DESCRIPTION_LENGTH - 1)}…`
          : description
      }
    >
      <Container>
        <PaperDetail paper={paper} version={shown?.number} />
        <RelatedPapers paper={paper} />
      </Container>
    </MetaLayout>
  );
}

const PaperPageQuery = graphql`
  query PaperPageQuery($id: String!) {
    paperByExternalId(id: $id) {
      arxivId
      venue
      title
      abstract
      versions {
        number
        title
        abstract
      }
      ...PaperDetailFragment
      ...RelatedPapersFragment
    }
  }
`;

// `$id` is an arXiv or OpenAlex (`W4417216520`) identifier. Old-style arXiv
// identifiers contain a slash (`quant-ph/9508027`), so they arrive as two path
// segments, the second one served by `$id.$number.tsx`.
export const loader =
  (environment: Environment) =>
  ({ params: { id, number } }: LoaderArgs) => {
    if (!id) {
      throw new Response("No paper ID", { status: 500 });
    }
    return preload<PaperPageQueryType>(environment, PaperPageQuery, {
      id: number ? `${id}/${number}` : id,
    });
  };

const usePaperPageQuery = () => usePreloaded<PaperPageQueryType>();
